/* Site config (soft-launch feature flags) admin panel — extracted from Admin.jsx tech-debt split. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";

export default function SiteConfigPanel() {
  const { refresh: refreshFlags } = useSiteFlags();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  if (!settings) return null;

  const save = async () => {
    setSaving(true);
    // Validate the two iCloud shortcut URL fields — a common (and
    // demoralising) mistake is pasting the user's PERSONAL SHARE TOKEN
    // (starts with `rfst_`) into what should be the iCloud installer URL.
    // The token belongs INSIDE the shortcut (in a Text action, per-user);
    // this field wants the public iCloud share link from Shortcuts.app →
    // "Copy iCloud Link". Guard against both mistakes with a clear toast.
    const validateIcloudUrl = (val, label) => {
      const v = (val || "").trim();
      if (!v) return true; // empty is fine — clears the CTA
      if (v.startsWith("rfst_") || v.includes("recipefiler.com/rfst_")) {
        toast.error(`That's your personal share token, not the iCloud URL.`, {
          description: `The ${label} field wants the public installer link from Shortcuts.app → Share icon → Copy iCloud Link (starts with https://www.icloud.com/shortcuts/). Your rfst_… token belongs INSIDE the shortcut, not here.`,
          duration: 10000,
        });
        return false;
      }
      if (!/^https:\/\/(www\.)?icloud\.com\/shortcuts\//i.test(v)) {
        toast.error(`${label} must be an iCloud shortcut URL`, {
          description: `Expected format: https://www.icloud.com/shortcuts/… — get this from Shortcuts.app → tap the share icon at the top of your shortcut → Copy iCloud Link.`,
          duration: 10000,
        });
        return false;
      }
      return true;
    };
    if (!validateIcloudUrl(settings.ios_shortcut_url, "token-based iCloud URL")) { setSaving(false); return; }
    if (!validateIcloudUrl(settings.ios_shortcut_url_simple, "URL-based iCloud URL")) { setSaving(false); return; }
    try {
      await api.patch("/settings", {
        subscriptions_enabled: !!settings.subscriptions_enabled,
        print_orders_enabled: !!settings.print_orders_enabled,
        gamification_enabled: !!settings.gamification_enabled,
        recipe_creator_enabled: !!settings.recipe_creator_enabled,
        referrals_enabled: !!settings.referrals_enabled,
        historical_recipes_enabled: !!settings.historical_recipes_enabled,
        meal_planner_enabled: !!settings.meal_planner_enabled,
        // Default true so a freshly saved config without the toggle enabled
        // doesn't flip hundreds of recipes' nutrition cards off silently.
        nutrition_estimates_enabled: settings.nutrition_estimates_enabled !== false,
        voice_search_enabled: settings.voice_search_enabled !== false,
        voice_nav_enabled: settings.voice_nav_enabled !== false,
        amazon_one_basket_enabled: !!settings.amazon_one_basket_enabled,
        voice_tour_enabled: !!settings.voice_tour_enabled,
        ios_shortcut_url: (settings.ios_shortcut_url || "").trim(),
        ios_shortcut_url_simple: (settings.ios_shortcut_url_simple || "").trim(),
        hide_hybrid_ios_method: !!settings.hide_hybrid_ios_method,
        sender_email: (settings.sender_email || "").trim(),
        reply_to_email: (settings.reply_to_email || "").trim(),
      });
      await refreshFlags();
      toast.success("Launch flags saved — site-wide CTAs updated");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="card-recipe">
        <p className="overline mb-2 text-rf-orange">Soft-launch feature flags</p>
        <h2 className="font-display text-2xl font-black text-rf-ink mb-4">Launch readiness</h2>
        <p className="text-sm text-rf-ink-soft mb-5">
          Turn these <b>off</b> at launch so the site runs in free-tier-only mode (1 cookbook, 10 recipe scans, no upgrade/order buttons).
          Turn them <b>on</b> once Stripe is live and Lulu print fulfillment is wired — subscription tiers + Lulu &quot;Order real book&quot; buttons will reappear automatically across the whole site.
        </p>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-subscriptions-row">
          <input
            type="checkbox"
            data-testid="flag-subscriptions"
            checked={!!settings.subscriptions_enabled}
            onChange={(e) => setSettings({ ...settings, subscriptions_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Subscriptions enabled <span className={`ml-2 chip ${settings.subscriptions_enabled ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.subscriptions_enabled ? "LIVE" : "OFF"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON (normal): Pricing page, &quot;Upgrade&quot; CTAs and Stripe checkout are active. Turn OFF only to pause new sign-ups to paid plans, for example during a Stripe outage. Existing members keep their plans either way.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-gamification-row">
          <input
            type="checkbox"
            data-testid="flag-gamification"
            checked={!!settings.gamification_enabled}
            onChange={(e) => setSettings({ ...settings, gamification_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Gamification enabled <span className={`ml-2 chip ${settings.gamification_enabled ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.gamification_enabled ? "LIVE" : "HIDDEN"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON: the Leaderboard nav link, cook profile pages (<code>/cook/:id</code>), and inline level chips on public recipes are visible to everyone. When OFF: the same routes 404 for the public and only Admin/SuperAdmin staff can preview them. Users still accumulate XP + streaks silently, so nothing is lost when you flip this on later.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-recipe-creator-row">
          <input
            type="checkbox"
            data-testid="flag-recipe-creator"
            checked={!!settings.recipe_creator_enabled}
            onChange={(e) => setSettings({ ...settings, recipe_creator_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Recipe Creator enabled <span className={`ml-2 chip ${settings.recipe_creator_enabled ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.recipe_creator_enabled ? "LIVE" : "HIDDEN"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON: the &quot;Recipe Creator&quot; nav link + <code>/recipe-creator</code> page are visible to everyone. Users list ingredients they have and get matched to public recipes ranked by how many of the recipe&apos;s ingredients they already own. When OFF: the page 404s for the public and only Admin/SuperAdmin staff can preview it.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-meal-planner-row">
          <input
            type="checkbox"
            data-testid="flag-meal-planner"
            checked={!!settings.meal_planner_enabled}
            onChange={(e) => setSettings({ ...settings, meal_planner_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Meal Planner enabled <span className={`ml-2 chip ${settings.meal_planner_enabled ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.meal_planner_enabled ? "LIVE" : "HIDDEN"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON: the &quot;Meal planner&quot; nav link + <code>/planner</code> page go live for every signed-in user. Weekly grid × 4 meals with tap-to-pick recipe drawer, silent auto-sync to the shopping list, copy last week, print the week and share by text. When OFF (default): the page 404s for the public and only Admin/SuperAdmin staff can preview at <code>/planner</code>.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-historical-row">
          <input
            type="checkbox"
            data-testid="flag-historical"
            checked={!!settings.historical_recipes_enabled}
            onChange={(e) => setSettings({ ...settings, historical_recipes_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Historical Recipes enabled <span className={`ml-2 chip ${settings.historical_recipes_enabled ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.historical_recipes_enabled ? "LIVE" : "HIDDEN"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON: the <code>/historical</code> route + nav link go live for everyone. Public-domain recipes from real cookbooks 1900 and earlier, searchable by country, name, A–Z, century and decade. Includes a gamified &quot;Historical Cook&quot; progression page (Time Traveler / World Passport / Ancient Scholar badges). Related FAQ entries also become visible. When OFF: /historical 404s for the public; Admin/SuperAdmin can still preview and curate content. Seed the initial 50+ verified recipes from the Historical Admin tab.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-referrals-row">
          <input
            type="checkbox"
            data-testid="flag-referrals"
            checked={!!settings.referrals_enabled}
            onChange={(e) => setSettings({ ...settings, referrals_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Referral programme enabled <span className={`ml-2 chip ${settings.referrals_enabled ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.referrals_enabled ? "LIVE" : "HIDDEN"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON: the &quot;Refer friends, earn recipes&quot; card appears on every user&apos;s Dashboard and a &quot;Have a referral code?&quot; field surfaces on the Sign Up page. Free-tier users earn <b>+5 recipe slots</b> for every referred user who adds their first recipe (max 15 qualifying = +75 slots). Paid subscribers still see the card so they can climb the leaderboard. When OFF: all referral UI is hidden and no bonuses are granted (pending referrals stay pending until you re-enable).</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-voice-search-row">
          <input
            type="checkbox"
            data-testid="flag-voice-search"
            checked={settings.voice_search_enabled !== false}
            onChange={(e) => setSettings({ ...settings, voice_search_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Voice search enabled <span className={`ml-2 chip ${settings.voice_search_enabled !== false ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.voice_search_enabled !== false ? "LIVE" : "OFF"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON (default): a floating mic FAB appears site-wide (hidden on Kitchen View, which has its own cook-mode voice stack). Each user still has to opt-in via Profile &rarr; Voice Settings before anything activates. When OFF: the FAB disappears everywhere, the <code>/api/voice/resolve</code> endpoint returns 503, and wake-word listening stops for every user. Kitchen View step/ingredient/scroll commands are unaffected (separate stack).</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-voice-nav-row">
          <input
            type="checkbox"
            data-testid="flag-voice-nav"
            checked={settings.voice_nav_enabled !== false}
            onChange={(e) => setSettings({ ...settings, voice_nav_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Show &quot;Voice&quot; in the top menu <span className={`ml-2 chip ${settings.voice_nav_enabled !== false ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.voice_nav_enabled !== false ? "SHOWN" : "HIDDEN"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When OFF, the Voice link is removed from the desktop navigation and the phone menu. The <code>/voice</code> page and all voice commands keep working.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="flag-amazon-basket-row">
          <input
            type="checkbox"
            data-testid="flag-amazon-basket"
            checked={!!settings.amazon_one_basket_enabled}
            onChange={(e) => setSettings({ ...settings, amazon_one_basket_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Amazon One Basket <span className={`ml-2 chip ${settings.amazon_one_basket_enabled ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.amazon_one_basket_enabled ? "LIVE" : "OFF"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON: on the Shopping List, &ldquo;Buy selected on Amazon&rdquo; puts every selected item that has a known Amazon product (ASIN) into ONE Amazon basket via Amazon&apos;s official Add-to-Cart form, tagged <code>boulevardie0d-21</code>. Items without an ASIN still open as separate searches. Users set an exact product by pasting an Amazon product link into &ldquo;Pick&rdquo;. Turn ON once your Amazon Creators API approval is received (PA-API 5.0 was retired 15 May 2026). When OFF (default): every selected item opens in its own tab.</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-5" data-testid="flag-print-orders-row">
          <input
            type="checkbox"
            data-testid="flag-print-orders"
            checked={!!settings.print_orders_enabled}
            onChange={(e) => setSettings({ ...settings, print_orders_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Lulu print orders enabled <span className={`ml-2 chip ${settings.print_orders_enabled ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.print_orders_enabled ? "LIVE" : "OFF"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON: &quot;Order real book at Lulu&quot; buttons appear on cookbooks. Generates a Lulu-compliant 6×9&quot; PDF, downloads it, then opens lulu.com so the user can upload + finalise their order. When OFF: the Lulu button is hidden everywhere (the free A4 ring-binder PDF stays available for all users).</p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-5" data-testid="flag-nutrition-row">
          <input
            type="checkbox"
            data-testid="flag-nutrition"
            checked={settings.nutrition_estimates_enabled !== false}
            onChange={(e) => setSettings({ ...settings, nutrition_estimates_enabled: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Nutrition estimates visible <span className={`ml-2 chip ${settings.nutrition_estimates_enabled !== false ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.nutrition_estimates_enabled !== false ? "LIVE" : "OFF"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ON: every recipe page shows an AI-drafted per-serving macro card below the method, including a persistent &quot;estimate only / not medical advice&quot; footer. When OFF: the card is hidden everywhere — existing data stays safe in the database and reappears instantly when you flip the switch back on.</p>
          </div>
        </label>

        <button
          data-testid="save-flags-btn"
          disabled={saving}
          onClick={save}
          className="btn-amber"
        >
          {saving ? "Saving…" : "Save launch flags"}
        </button>
      </div>

      <div className="card-recipe" data-testid="email-sender-card">
        <p className="overline mb-2 text-rf-orange">Email sender — from address &amp; reply-to</p>
        <h2 className="font-display text-2xl font-black text-rf-ink mb-4">Sender identity</h2>
        <p className="text-sm text-rf-ink-soft mb-4 leading-relaxed">
          Controls the <b>From</b> and <b>Reply-To</b> headers on every outbound Recipe Filer email — gift codes, password resets, cookbook invites, print receipts.
          Leave a field empty to fall back to the env var (or the Resend sandbox default). <b>Save this the moment your Resend domain finishes verifying</b> — no redeploy needed.
        </p>
        <label className="block text-xs font-bold text-rf-ink-soft uppercase tracking-wider mb-1">From address</label>
        <input
          type="text"
          data-testid="sender-email-input"
          value={settings.sender_email || ""}
          onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
          placeholder='e.g. Recipe Filer <hello@send.recipefiler.com>'
          className="input-rf mb-3 font-mono text-sm"
        />
        <label className="block text-xs font-bold text-rf-ink-soft uppercase tracking-wider mb-1">Reply-To address</label>
        <input
          type="email"
          data-testid="reply-to-email-input"
          value={settings.reply_to_email || ""}
          onChange={(e) => setSettings({ ...settings, reply_to_email: e.target.value })}
          placeholder="e.g. contactus@recipefiler.com"
          className="input-rf mb-3 font-mono text-sm"
        />
        <button
          data-testid="save-sender-btn"
          disabled={saving}
          onClick={save}
          className="btn-outline-ink"
        >
          {saving ? "Saving…" : "Save sender"}
        </button>
        <p className="text-[11px] text-rf-ink-soft mt-3 italic">Tip: use a subdomain (<code>send.recipefiler.com</code>) not the root — Resend recommends it and it protects your root-domain reputation. See Admin › Stats for a live &ldquo;Email wiring&rdquo; card + &ldquo;Send test email&rdquo; button.</p>
      </div>

      <div className="card-recipe" data-testid="ios-hide-hybrid-card">
        <p className="overline mb-2 text-rf-orange">iOS share tutorial visibility</p>
        <h2 className="font-display text-2xl font-black text-rf-ink mb-3">Which method(s) do users see?</h2>
        <p className="text-sm text-rf-ink-soft mb-4 leading-relaxed">
          By default the <b>/ios-share-setup</b> page shows the hybrid Safari + clipboard method first and hides the token method behind a &ldquo;Show power-user setup&rdquo; toggle. Tick the box below to <b>hide the hybrid method entirely</b> and make the token-based tutorial the only path — cleaner for users, and it&apos;s the method you&apos;ve been testing on production.
        </p>
        <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-2" data-testid="flag-hide-hybrid-row">
          <input
            type="checkbox"
            data-testid="flag-hide-hybrid"
            checked={!!settings.hide_hybrid_ios_method}
            onChange={(e) => setSettings({ ...settings, hide_hybrid_ios_method: e.target.checked })}
            className="mt-1 w-5 h-5 accent-rf-orange"
          />
          <div className="flex-1">
            <p className="font-bold text-rf-ink">Hide the hybrid Safari/clipboard method <span className={`ml-2 chip ${settings.hide_hybrid_ios_method ? "!bg-emerald-500/20 !text-emerald-800" : "!bg-rf-cream !text-rf-ink-soft"}`}>{settings.hide_hybrid_ios_method ? "HIDDEN" : "VISIBLE"}</span></p>
            <p className="text-xs text-rf-ink-soft mt-1">When ticked: users only see the token-based Shortcut tutorial (with a nice hero + how-to-use section auto-expanded). When unticked (default): the hybrid method shows first, and the token method sits behind a &ldquo;Show power-user setup&rdquo; toggle.</p>
          </div>
        </label>
        <button
          data-testid="save-hide-hybrid-btn"
          disabled={saving}
          onClick={save}
          className="btn-outline-ink mt-2"
        >
          {saving ? "Saving…" : "Save visibility"}
        </button>
      </div>

      <div className="card-recipe">
        <p className="overline mb-2 text-rf-orange">iOS one-tap share — recommended (zero paste)</p>
        <h2 className="font-display text-2xl font-black text-rf-ink mb-4">Apple Shortcut — hybrid share-sheet + clipboard</h2>
        <p className="text-sm text-rf-ink-soft mb-4">
          This is the <b>recommended zero-paste path</b>. Build a 4-action shortcut that opens Safari to <code className="text-xs bg-rf-cream-alt px-1 py-0.5 rounded">https://recipefiler.com/share-import?url=&#123;URL&#125;</code>. The magic is action #1 — <b>Get URLs from Input</b> with the &ldquo;If there&apos;s no input&rdquo; fallback set to <b>Clipboard</b>. That single setting means the shortcut works from BOTH the Share Sheet (Safari, TikTok, YouTube) AND from a Home-Screen tap after Copy-Link (the reliable path for Instagram Reels). No token, no editing, no per-user config — users just install and go.
        </p>
        <input
          type="url"
          data-testid="ios-shortcut-url-simple-input"
          value={settings.ios_shortcut_url_simple || ""}
          onChange={(e) => setSettings({ ...settings, ios_shortcut_url_simple: e.target.value })}
          placeholder="https://www.icloud.com/shortcuts/... (the URL-based shortcut)"
          className="input-rf mb-3"
        />
        {settings.ios_shortcut_url_simple && !/^https:\/\/(www\.)?icloud\.com\/shortcuts\//i.test((settings.ios_shortcut_url_simple || "").trim()) && (
          <div data-testid="ios-shortcut-url-simple-warning" className="text-xs text-red-800 bg-red-50 border-2 border-red-500/60 rounded-lg p-3 mb-3">
            <b>⚠ This isn&apos;t a valid iCloud shortcut URL.</b> Currently saved value doesn&apos;t start with <code>https://www.icloud.com/shortcuts/</code>. Users clicking &ldquo;Install shortcut&rdquo; will see a blank page. Fix: on iPhone, open Shortcuts.app → tap the shortcut → tap the ⤴ share icon at the top → <b>Copy iCloud Link</b>. Paste that above.
          </div>
        )}
        <div className="flex items-center gap-3 mb-3">
          <button
            data-testid="save-ios-shortcut-simple-btn"
            disabled={saving}
            onClick={save}
            className="btn-amber"
          >
            {saving ? "Saving…" : "Save URL-based iCloud URL"}
          </button>
          {settings.ios_shortcut_url_simple ? (
            <a href={settings.ios_shortcut_url_simple} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-rf-orange underline">Preview link →</a>
          ) : null}
        </div>
        <details className="text-xs text-rf-ink-soft bg-rf-cream-alt rounded-xl p-3">
          <summary className="cursor-pointer font-bold text-rf-ink">Build the hybrid shortcut (90-second one-time build)</summary>
          <ol className="list-decimal list-inside mt-2 space-y-1 leading-snug">
            <li>On iPhone open <b>Shortcuts.app</b> → tap <b>+</b> → name it &ldquo;Save to Recipe Filer&rdquo;.</li>
            <li>Add <b>Get URLs from Input</b> (Web). Tap the greyed <b>&ldquo;If there&apos;s no input&rdquo;</b> pill and change it to <b>Clipboard</b> — this is what makes Instagram Reels work.</li>
            <li>Add <b>URL Encode</b> — auto-picks the URLs variable.</li>
            <li>Add <b>Text</b> — paste <code className="text-[11px] bg-white px-1 py-0.5 rounded border">https://recipefiler.com/share-import?url=</code> then insert the <b>URL Encoded</b> pill right after <code>?url=</code>. <b>Use Text, not URL</b> — URL action drops the query string.</li>
            <li>Add <b>Open URLs</b>.</li>
            <li>Tap the <b>ⓘ</b> settings → toggle <b>Show in Share Sheet</b> ON → under <b>Share Sheet Types</b> tick <b>URLs</b> only → <b>Done</b>.</li>
            <li>Tap the share icon at the top of the shortcut editor → <b>Copy iCloud Link</b>. Paste that URL above and hit Save.</li>
          </ol>
        </details>
      </div>

      <div className="card-recipe">
        <p className="overline mb-2 text-rf-orange">iOS one-tap share — silent (power user)</p>
        <h2 className="font-display text-2xl font-black text-rf-ink mb-4">Apple Shortcut — token-based iCloud link</h2>
        <p className="text-sm text-rf-ink-soft mb-4">
          Optional. Silent background version — no Safari flash — but requires the user to paste their token once inside the Shortcut editor (Apple doesn&apos;t allow us to pre-embed secrets in shared shortcuts). Leave empty if you only want to offer the URL-based path above.
        </p>
        <input
          type="url"
          data-testid="ios-shortcut-url-input"
          value={settings.ios_shortcut_url || ""}
          onChange={(e) => setSettings({ ...settings, ios_shortcut_url: e.target.value })}
          placeholder="https://www.icloud.com/shortcuts/... (the token-based shortcut)"
          className="input-rf mb-3"
        />
        {settings.ios_shortcut_url && !/^https:\/\/(www\.)?icloud\.com\/shortcuts\//i.test((settings.ios_shortcut_url || "").trim()) && (
          <div data-testid="ios-shortcut-url-warning" className="text-xs text-red-800 bg-red-50 border-2 border-red-500/60 rounded-lg p-3 mb-3">
            <b>⚠ This isn&apos;t a valid iCloud shortcut URL.</b> Currently saved value doesn&apos;t start with <code>https://www.icloud.com/shortcuts/</code>. Common mistake: pasting a personal share token (<code>rfst_…</code>) here — that token belongs INSIDE the shortcut, not in this field. Fix: on iPhone, open Shortcuts.app → tap the shortcut → tap the ⤴ share icon at the top → <b>Copy iCloud Link</b>. Paste that above.
          </div>
        )}
        <div className="flex items-center gap-3 mb-3">
          <button
            data-testid="save-ios-shortcut-btn"
            disabled={saving}
            onClick={save}
            className="btn-amber"
          >
            {saving ? "Saving…" : "Save iCloud URL"}
          </button>
          {settings.ios_shortcut_url ? (
            <a
              href={settings.ios_shortcut_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="preview-ios-shortcut-link"
              className="text-sm font-bold text-rf-orange underline"
            >
              Preview link →
            </a>
          ) : null}
        </div>
        <p className="text-[11px] text-rf-ink-soft italic mb-3">
          Tip: open the URL in Safari on your iPhone first to confirm it loads. If Apple shows &quot;Shortcut Not Found&quot;, the iCloud link is stale — re-share the shortcut and paste the fresh URL here.
        </p>
        <details className="text-xs text-rf-ink-soft bg-rf-cream-alt rounded-xl p-3">
          <summary className="cursor-pointer font-bold text-rf-ink">How to create + share the token-based shortcut</summary>
          <ol className="list-decimal list-inside mt-2 space-y-1 leading-snug">
            <li>On iPhone or Mac open <b>Shortcuts.app</b> → tap <b>+</b> → name it &ldquo;Save to Recipe Filer&rdquo;.</li>
            <li>Add action <b>Text</b> containing exactly <code>PASTE_YOUR_TOKEN_HERE</code>.</li>
            <li>Add action <b>Get Contents of URL</b> with URL <code>https://recipefiler.com/api/share-import-token</code>, method <b>POST</b>, headers <code>Content-Type: application/json</code>, JSON body with fields <code>token</code> (the Text variable) and <code>url</code> (Shortcut Input).</li>
            <li>Add action <b>Show Notification</b> — title &ldquo;Recipe saved!&rdquo;.</li>
            <li>Open the ⓘ settings → toggle ON <b>Show in Share Sheet</b>.</li>
            <li>Tap the share icon → <b>Copy iCloud Link</b>. Paste above and Save.</li>
          </ol>
        </details>
      </div>
    </div>
  );
}
